import axios from 'axios';
import loadingManager from './loadingService';

// Get API URL from runtime config or fallback to relative URL
const API_URL = window.CAHOOTS_CONFIG?.API_URL || '/api';

class UnifiedApiClient {
  constructor() {
    // Create Axios instance with base configuration
    this.client = axios.create({
      baseURL: API_URL,
      timeout: 30000,
      withCredentials: true, // Important: Always send cookies with requests
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor - add auth headers
    this.client.interceptors.request.use(
      (config) => {
        // Get token from httpOnly cookie via API call if needed
        // For now, fallback to localStorage but migrate to cookies
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add user context header if available
        const user = this.getCurrentUser();
        if (user?.id) {
          config.headers['X-User-ID'] = user.id;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle errors and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (!originalRequest) return Promise.reject(error);

        const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                               originalRequest.url?.includes('/auth/register') ||
                               originalRequest.url?.includes('/auth/link-google') ||
                               originalRequest.url?.includes('/auth/google/exchange');

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          originalRequest._retry = true;

          try {
            await this.refreshToken();
            const token = this.getAuthToken();
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            this.handleAuthError();
            return Promise.reject(error);
          }
        }

        return Promise.reject(this.enhanceError(error));
      }
    );
  }

  // Enhanced error with user-friendly messages
  enhanceError(error) {
    // Preserve the original error and explicitly copy important axios properties
    // Spread operator may not copy all properties from Error objects
    const enhanced = {
      ...error,
      response: error.response,
      request: error.request,
      config: error.config,
      status: error.response?.status,
      message: error.message,
    };

    if (error.response) {
      // Server responded with error status
      enhanced.userMessage = this.getErrorMessage(error.response.status, error.response.data);
    } else if (error.request) {
      // Network error
      enhanced.userMessage = 'Network error. Please check your connection.';
    } else {
      // Request setup error
      enhanced.userMessage = 'Request failed. Please try again.';
    }

    return enhanced;
  }

  getErrorMessage(status, data) {
    const statusMessages = {
      400: 'Invalid request. Please check your input.',
      401: 'Please log in to continue.',
      403: 'You don\'t have permission to perform this action.',
      404: 'The requested resource was not found.',
      500: 'Server error. Please try again later.',
      503: 'Service temporarily unavailable. Please try again later.'
    };

    return data?.detail || data?.message || statusMessages[status] || 'An error occurred.';
  }

  // Auth token management (to be migrated to httpOnly cookies)
  getAuthToken() {
    const token = localStorage.getItem('token');
    if (token) {
      return token;
    }

    // Check if user has explicitly logged out
    const hasLoggedOut = localStorage.getItem('has_logged_out') === 'true';
    if (hasLoggedOut) {
      return null;
    }

    // Development bypass for when no real token is available
    if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
      // Store dev-bypass-token so it persists across page refreshes
      localStorage.setItem('token', 'dev-bypass-token');
      return 'dev-bypass-token';
    }

    return null;
  }

  setAuthToken(token) {
    if (token) {
      localStorage.setItem('token', token);
      // Update the axios client default headers immediately
      this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem('token');
      // Remove authorization header
      delete this.client.defaults.headers.common['Authorization'];
    }
  }

  getCurrentUser() {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }

  async refreshToken() {
    // Use a separate axios instance to avoid interceptor loops
    const refreshClient = axios.create({
      baseURL: API_URL,
      withCredentials: true, // Important for cookies
      timeout: 10000
    });

    try {

      // In development, include the dev-bypass-token in the header
      const headers = {};
      const currentToken = this.getAuthToken();
      if (currentToken === 'dev-bypass-token') {
        headers.Authorization = `Bearer ${currentToken}`;
      }

      // Get refresh token from localStorage
      const storedRefreshToken = localStorage.getItem('refresh_token');

      // Make request to refresh token endpoint with refresh token in body
      const response = await refreshClient.post('/auth/refresh-token',
        { refresh_token: storedRefreshToken },
        { headers }
      );
      const { access_token, refresh_token: newRefreshToken } = response.data;

      if (!access_token) {
        throw new Error('No access token returned');
      }

      this.setAuthToken(access_token);

      // Store new refresh token if provided
      if (newRefreshToken) {
        localStorage.setItem('refresh_token', newRefreshToken);
      }

      // Fetch user data with new token to ensure auth context is updated
      try {
        const userResponse = await this.client.get('/auth/me');
        if (userResponse.data) {
          localStorage.setItem('user', JSON.stringify(userResponse.data));
        }
      } catch (userError) {
        // Failed to refresh user data
      }

      return access_token;
    } catch (error) {
      // Token refresh failed
      // Clear invalid tokens
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      throw error;
    }
  }

  handleAuthError() {
    this.setAuthToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('refresh_token');
    // Prevent dev-bypass-token from being auto-recreated
    localStorage.setItem('has_logged_out', 'true');

    // Use React Router navigation instead of direct window.location
    // This will be handled by the auth context
    if (window.__CAHOOTS_NAVIGATE) {
      window.__CAHOOTS_NAVIGATE('/login');
    } else {
      // Fallback for cases where navigate is not available
      window.location.href = '/login';
    }
  }

  // HTTP Methods with consistent error handling
  async get(url, config = {}) {
    try {
      const response = await this.client.get(url, config);
      return response.data;
    } catch (error) {
      throw this.enhanceError(error);
    }
  }

  async post(url, data, config = {}) {
    try {
      const response = await this.client.post(url, data, config);
      return response.data;
    } catch (error) {
      throw this.enhanceError(error);
    }
  }

  async patch(url, data, config = {}) {
    try {
      const response = await this.client.patch(url, data, config);
      return response.data;
    } catch (error) {
      throw this.enhanceError(error);
    }
  }

  async put(url, data, config = {}) {
    try {
      const response = await this.client.put(url, data, config);
      return response.data;
    } catch (error) {
      throw this.enhanceError(error);
    }
  }

  async delete(url, config = {}) {
    try {
      const response = await this.client.delete(url, config);
      return response.data;
    } catch (error) {
      throw this.enhanceError(error);
    }
  }

  // Methods with loading state management
  async getWithLoading(url, loadingType, config = {}) {
    loadingManager.setLoading(loadingType, true);
    try {
      return await this.get(url, config);
    } finally {
      loadingManager.setLoading(loadingType, false);
    }
  }

  async postWithLoading(url, data, loadingType, config = {}) {
    loadingManager.setLoading(loadingType, true);
    try {
      return await this.post(url, data, config);
    } finally {
      loadingManager.setLoading(loadingType, false);
    }
  }

  async patchWithLoading(url, data, loadingType, config = {}) {
    loadingManager.setLoading(loadingType, true);
    try {
      return await this.patch(url, data, config);
    } finally {
      loadingManager.setLoading(loadingType, false);
    }
  }

  // Specialized methods for common operations
  async getPlans(params = {}) {
    return this.get('/plans', { params });
  }

  // Backward compat aliases
  async getTasks(params = {}) {
    return this.getPlans(params);
  }

  async getPlan(planId) {
    return this.get(`/plans/${planId}`);
  }

  async getTask(planId) {
    return this.getPlan(planId);
  }

  async getPlanTree(planId) {
    return this.get(`/plans/${planId}/tree`);
  }

  async getTaskTree(planId) {
    return this.getPlanTree(planId);
  }

  async createPlan(planData) {
    return this.post('/plans', planData);
  }

  async createTask(planData) {
    return this.createPlan(planData);
  }

  async updatePlan(planId, updates) {
    return this.patch(`/plans/${planId}`, updates);
  }

  async updateTask(planId, updates) {
    return this.updatePlan(planId, updates);
  }

  async deletePlan(planId) {
    return this.delete(`/plans/${planId}`);
  }

  async deleteTask(planId) {
    return this.deletePlan(planId);
  }

  async getPlanStats(topLevelOnly = true) {
    return this.get('/plans/stats', {
      params: { top_level_only: topLevelOnly }
    });
  }

  async getTaskStats(topLevelOnly = true) {
    return this.getPlanStats(topLevelOnly);
  }

  // ============================================
  // Decision Cascade Preview/Apply
  // ============================================

  async previewDecisionCascade(planId, decisionId, body) {
    return this.post(`/plans/${planId}/decisions/${decisionId}/preview`, body);
  }

  async applyDecisionCascade(planId, decisionId, body) {
    return this.post(`/plans/${planId}/decisions/${decisionId}/apply`, body);
  }

  // ============================================
  // Plan Chat Methods
  // ============================================

  /**
   * Get all conversations for a plan
   * @param {string} planId - The plan ID
   * @returns {Promise<object>} - { conversations, active_conversation_id }
   */
  async getConversations(planId) {
    return this.get(`/plans/${planId}/chat/conversations`);
  }

  /**
   * Create a new conversation
   * @param {string} planId - The plan ID
   * @param {string} name - Optional conversation name
   * @returns {Promise<object>} - { conversation }
   */
  async createConversation(planId, name = null) {
    const body = name ? { name } : {};
    return this.post(`/plans/${planId}/chat/conversations`, body);
  }

  /**
   * Get a specific conversation with messages
   * @param {string} planId - The plan ID
   * @param {string} conversationId - The conversation ID
   * @param {number} limit - Max messages to return (default 50)
   * @returns {Promise<object>} - { conversation, messages }
   */
  async getConversation(planId, conversationId, limit = 50) {
    return this.get(`/plans/${planId}/chat/conversations/${conversationId}`, {
      params: { limit }
    });
  }

  /**
   * Rename a conversation
   * @param {string} planId - The plan ID
   * @param {string} conversationId - The conversation ID
   * @param {string} name - New conversation name
   * @returns {Promise<object>} - { conversation }
   */
  async renameConversation(planId, conversationId, name) {
    return this.patch(`/plans/${planId}/chat/conversations/${conversationId}`, { name });
  }

  /**
   * Delete a conversation
   * @param {string} planId - The plan ID
   * @param {string} conversationId - The conversation ID
   * @returns {Promise<object>} - { success, message }
   */
  async deleteConversation(planId, conversationId) {
    return this.delete(`/plans/${planId}/chat/conversations/${conversationId}`);
  }

  /**
   * Get chat history for a plan (legacy - uses default conversation)
   * @param {string} planId - The plan ID
   * @param {number} limit - Maximum messages to return (default 50)
   * @returns {Promise<object>} - { messages, total_count }
   */
  async getChatHistory(planId, limit = 50) {
    return this.get(`/plans/${planId}/chat/history`, {
      params: { limit }
    });
  }

  /**
   * Clear chat history for a plan (legacy - clears default conversation)
   * @param {string} planId - The plan ID
   * @returns {Promise<object>} - { success, message }
   */
  async clearChatHistory(planId) {
    return this.delete(`/plans/${planId}/chat/history`);
  }

  /**
   * Get the URL for sending chat messages to a specific conversation
   * @param {string} planId - The plan ID
   * @param {string} conversationId - Optional conversation ID
   * @returns {string} - The full URL for the chat endpoint
   */
  getChatMessagesUrl(planId, conversationId = null) {
    if (conversationId) {
      return `${API_URL}/plans/${planId}/chat/conversations/${conversationId}/messages`;
    }
    return `${API_URL}/plans/${planId}/chat/messages`;
  }

  /**
   * Get authorization headers for fetch requests
   * Used when making direct fetch calls (e.g., for SSE streaming)
   * @returns {object} - Headers object with Authorization
   */
  getAuthHeaders() {
    const token = this.getAuthToken();
    return {
      'Authorization': token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    };
  }

  /**
   * Approve a change proposal from the chat agent
   * @param {string} planId - The plan ID
   * @param {string} conversationId - The conversation ID
   * @param {string} proposalId - The proposal ID to approve
   * @param {number[]} changeIds - Array of change indices to apply (empty = all)
   * @returns {Promise<object>} - { success, applied_count, skipped_count, errors, new_artifact_ids }
   */
  async approveProposal(planId, conversationId, proposalId, changeIds = []) {
    return this.post(
      `/plans/${planId}/chat/conversations/${conversationId}/proposals/${proposalId}/approve`,
      { change_ids: changeIds }
    );
  }

  /**
   * Reject a change proposal from the chat agent
   * @param {string} planId - The plan ID
   * @param {string} conversationId - The conversation ID
   * @param {string} proposalId - The proposal ID to reject
   * @returns {Promise<object>} - { success, message }
   */
  async rejectProposal(planId, conversationId, proposalId) {
    return this.post(
      `/plans/${planId}/chat/conversations/${conversationId}/proposals/${proposalId}/reject`,
      {}
    );
  }

  /**
   * Approve a change proposal from the idea chat agent
   */
  async approveIdeaProposal(ideaId, conversationId, proposalId, changeIds = []) {
    return this.post(
      `/ideas/${ideaId}/chat/conversations/${conversationId}/proposals/${proposalId}/approve`,
      { change_ids: changeIds }
    );
  }

  /**
   * Reject a change proposal from the idea chat agent
   */
  async rejectIdeaProposal(ideaId, conversationId, proposalId) {
    return this.post(
      `/ideas/${ideaId}/chat/conversations/${conversationId}/proposals/${proposalId}/reject`,
      {}
    );
  }

  // ============================================
  // Idea Chat Methods (idea-scoped)
  // ============================================

  async getIdeaConversations(ideaId) {
    return this.get(`/ideas/${ideaId}/chat/conversations`);
  }

  async createIdeaConversation(ideaId, name = null) {
    const body = name ? { name } : {};
    return this.post(`/ideas/${ideaId}/chat/conversations`, body);
  }

  async getIdeaConversation(ideaId, conversationId) {
    return this.get(`/ideas/${ideaId}/chat/conversations/${conversationId}`);
  }

  async renameIdeaConversation(ideaId, conversationId, name) {
    return this.patch(`/ideas/${ideaId}/chat/conversations/${conversationId}`, { name });
  }

  async deleteIdeaConversation(ideaId, conversationId) {
    return this.delete(`/ideas/${ideaId}/chat/conversations/${conversationId}`);
  }

  getIdeaChatMessagesUrl(ideaId, conversationId) {
    return `${API_URL}/ideas/${ideaId}/chat/conversations/${conversationId}/messages`;
  }

  // ============================================
  // Account Linking Methods
  // ============================================

  /**
   * Link a Google account to an existing local account
   * @param {string} googleToken - The Google OAuth access token
   * @param {string} password - The user's local account password
   * @returns {Promise<object>} - { access_token, user }
   */
  async linkGoogleAccount(googleToken, password) {
    return this.post('/auth/link-google', {
      google_token: googleToken,
      password: password,
    });
  }

  // ============================================
  // Auth Flow Methods
  // ============================================

  /**
   * Check if an email address is already registered
   * Used by the auth flow to determine login vs signup
   * @param {string} email - The email address to check
   * @returns {Promise<object>} - { exists: boolean, provider: string|null }
   */
  async checkEmailExists(email) {
    return this.post('/auth/check-email', { email });
  }

  /**
   * Create a Stripe checkout session for subscription upgrade
   * @param {object} options - Checkout options
   * @param {string} options.priceId - Stripe price ID for the plan
   * @param {string} options.successUrl - URL to redirect on success
   * @param {string} options.cancelUrl - URL to redirect on cancel
   * @returns {Promise<object>} - { sessionId, url }
   */
  async createCheckoutSession(options) {
    // Map the options to match the backend API
    return this.post('/subscriptions/checkout', {
      price_id: options.priceId,
      success_url: options.successUrl,
      cancel_url: options.cancelUrl,
    });
  }
}

// Export singleton instance
const apiClient = new UnifiedApiClient();
export default apiClient;
